import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Mail, Phone } from 'lucide-react';

const QUICK_LINKS = [
  { label: 'Find a Cemetery', to: '/cemeteries' },
  { label: 'About Us', to: '/about' },
  { label: 'Our Services', to: '/services' },
  { label: 'Our Partners', to: '/partners' },
  { label: 'Contact', to: '/contact' },
];

// These used to point at /services/interment, /services/memorial and friends —
// none of which are routes, so every one of them rendered a blank page. They
// are sections of the services page, so that is where they now go.
const SERVICES = [
  { label: 'Burial Services', to: '/services' },
  { label: 'Cremation Services', to: '/services' },
  { label: 'Cemetery Planning', to: '/services' },
  { label: 'Pre-Planning', to: '/services' },
];

const footerLink =
  'font-montserrat text-sm text-slate-600 transition-colors hover:text-navy-800';

const Footer = () => (
  <footer className="relative border-t border-slate-200 bg-white">
    {/* Brand hairline that ties the foot of every page back to the logo blue. */}
    <div className="h-1 w-full bg-gradient-to-r from-navy-900 via-navy-700 to-navy-500" />

    <div className="container mx-auto px-4 py-16">
      <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
        {/* Company */}
        <div className="md:col-span-4">
          <Link to="/" aria-label="Cemetery & Burial Solutions — home">
            <img
              src="/images/navbar/logo.png"
              alt="Cemetery & Burial Solutions"
              className="h-16 w-auto"
            />
          </Link>
          <p className="mt-6 max-w-sm font-montserrat text-sm leading-relaxed text-slate-600">
            Honoring lives with dignity and respect. Providing comprehensive cemetery services
            with compassion and care throughout our communities.
          </p>
          <Link to="/contact" className="btn-primary mt-7">
            Contact Us
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="md:col-span-2">
          <h2 className="mb-5 font-montserrat text-xs font-semibold uppercase tracking-[0.2em] text-navy-900">
            Quick Links
          </h2>
          <ul className="space-y-3">
            {QUICK_LINKS.map((link) => (
              <li key={link.label}>
                <Link to={link.to} className={footerLink}>
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <h2 className="mb-5 font-montserrat text-xs font-semibold uppercase tracking-[0.2em] text-navy-900">
            Services
          </h2>
          <ul className="space-y-3">
            {SERVICES.map((link) => (
              <li key={link.label}>
                <Link to={link.to} className={footerLink}>
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div className="md:col-span-3">
          <h2 className="mb-5 font-montserrat text-xs font-semibold uppercase tracking-[0.2em] text-navy-900">
            Contact Information
          </h2>
          <div className="space-y-5">
            <div className="flex gap-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy-50 text-navy-700 ring-1 ring-inset ring-navy-100">
                <Mail className="h-4 w-4" />
              </span>
              <div>
                <p className="font-montserrat text-sm font-medium text-slate-800">Email Us</p>
                <a
                  href="mailto:woollsjack@cemeteryburialsolutions.org"
                  className={`${footerLink} break-all`}
                >
                  woollsjack@cemeteryburialsolutions.org
                </a>
              </div>
            </div>

            <div className="flex gap-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy-50 text-navy-700 ring-1 ring-inset ring-navy-100">
                <Phone className="h-4 w-4" />
              </span>
              <div>
                <p className="font-montserrat text-sm font-medium text-slate-800">Call Us</p>
                {/* Previously styled as buttons that did nothing when clicked. */}
                <a href="tel:8003700969" className={`${footerLink} block`}>
                  (800) 370-0969
                </a>
                <a href="tel:4437710498" className={`${footerLink} block`}>
                  (443) 771-0498
                </a>
              </div>
            </div>

            <div className="flex gap-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy-50 text-navy-700 ring-1 ring-inset ring-navy-100">
                <Clock className="h-4 w-4" />
              </span>
              <div>
                <p className="font-montserrat text-sm font-medium text-slate-800">Hours</p>
                <p className="font-montserrat text-sm text-slate-600">
                  Available 24 hours, 7 days a week
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="border-t border-slate-200 bg-slate-50">
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <p className="font-montserrat text-sm text-slate-500">
            &copy; {new Date().getFullYear()} Cemetery &amp; Burial Solutions. All rights
            reserved.
          </p>
          {/* Privacy Policy and Terms of Service links were removed: they pointed
              at routes that do not exist. Restore them once the real policies are
              written. */}
          <Link
            to="/admin/cemeteries"
            className="font-montserrat text-sm text-slate-500 transition-colors hover:text-navy-800"
          >
            Staff Login
          </Link>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
